import React, { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { Input } from "@/components/ui/input";
import { Search, ArrowRight, Clock, Sparkles, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";

/**
 * /insights — public listing of Connexis Insights articles.
 *
 * Design language: matches the premium orbital aesthetic from /pay-per-call —
 * a dark editorial header with a big headline + live search, followed by a
 * clean 3-col card grid on a light body. Sector-tag chips filter the grid.
 */
export default function Insights() {
  const [params, setParams] = useSearchParams();
  const activeTag = params.get("tag") || "";
  const [q, setQ] = useState(params.get("q") || "");
  const [data, setData] = useState({ items: [], total: 0, tags: [] });
  const [loading, setLoading] = useState(true);

  useSeo({
    title: "Insights · Connexis",
    description: "Fresh writing from the Connexis desk on pay-per-call, compliance, routing and buyer operations.",
    canonical: `${(process.env.REACT_APP_SITE_URL || "https://connex.is").replace(/\/+$/, "")}/insights`,
  });

  // Debounced fetch
  useEffect(() => {
    let cancelled = false;
    const id = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get("/insights", { params: { q: q || undefined, sector: activeTag || undefined, limit: 30 } });
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData({ items: [], total: 0, tags: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(id); };
  }, [q, activeTag]);

  const setTag = (tag) => {
    const next = new URLSearchParams(params);
    if (tag) next.set("tag", tag);
    else next.delete("tag");
    setParams(next, { replace: true });
  };

  const [feature, ...rest] = data.items;

  return (
    <MarketingLayout>
      <div className="bg-white" data-testid="insights-page">
        {/* ─── HERO ────────────────────────────────────────────── */}
        <section className="relative overflow-hidden bg-[#070c1a] text-white">
          <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
               style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
          <div className="absolute -top-40 left-1/3 w-[620px] h-[620px] rounded-full bg-[#0055FF]/[0.18] blur-[120px] pointer-events-none" />
          <div className="absolute -bottom-32 -right-20 w-[520px] h-[520px] rounded-full bg-emerald-500/[0.08] blur-[120px] pointer-events-none" />

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 pb-16 lg:pt-24 lg:pb-20">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase text-[#7da9ff] font-semibold">
                <Sparkles className="size-3" />
                Connexis Insights
              </div>
              <h1 className="mt-3 text-5xl sm:text-6xl lg:text-[64px] tracking-[-0.045em] font-semibold text-white leading-[1.02]">
                Writing from{" "}
                <span className="bg-gradient-to-r from-white via-[#7da9ff] to-emerald-300 bg-clip-text text-transparent">
                  the routing layer.
                </span>
              </h1>
              <p className="mt-5 max-w-xl text-[17px] text-white/60 leading-relaxed">
                Field notes from the desk that routes 240,000 enquiries a month. Rate cards, compliance
                breakdowns, playbooks and the occasional strong opinion.
              </p>

              {/* Search + tag chips */}
              <div className="mt-8 flex flex-col gap-4">
                <div className="relative max-w-md">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-white/40" />
                  <Input
                    data-testid="insights-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search articles…"
                    className="pl-10 h-11 bg-white/[0.04] border-white/10 text-white placeholder:text-white/30 focus:border-[#0055FF]/60 focus-visible:ring-[#0055FF]/40 focus-visible:ring-offset-0"
                  />
                </div>
                {data.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" data-testid="insights-tag-chips">
                    <button
                      type="button"
                      onClick={() => setTag("")}
                      className={`px-3 py-1.5 rounded-full border text-[11.5px] font-medium transition-colors ${!activeTag ? "border-white/60 bg-white text-slate-900" : "border-white/10 bg-white/[0.03] text-white/70 hover:border-white/25 hover:text-white"}`}
                      data-testid="insights-tag-all"
                    >
                      All
                      <span className="ml-1.5 text-white/40 tabular-nums">{data.total}</span>
                    </button>
                    {data.tags.map((t) => (
                      <button
                        key={t.tag}
                        type="button"
                        onClick={() => setTag(t.tag === activeTag ? "" : t.tag)}
                        className={`px-3 py-1.5 rounded-full border text-[11.5px] font-medium transition-colors ${activeTag === t.tag ? "border-white/60 bg-white text-slate-900" : "border-white/10 bg-white/[0.03] text-white/70 hover:border-white/25 hover:text-white"}`}
                        data-testid={`insights-tag-${t.tag.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        {t.tag}
                        <span className={`ml-1.5 tabular-nums ${activeTag === t.tag ? "text-slate-500" : "text-white/40"}`}>{t.count}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ─── LIST ────────────────────────────────────────────── */}
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          {loading ? (
            <div className="grid place-items-center py-24 text-slate-400">
              <Loader2 className="size-6 animate-spin" />
            </div>
          ) : data.items.length === 0 ? (
            <EmptyState q={q} tag={activeTag} clear={() => { setQ(""); setTag(""); }} />
          ) : (
            <>
              {/* Featured article */}
              {feature && !q && !activeTag && (
                <FeaturedCard article={feature} />
              )}
              {/* Grid — always renders remaining items; when no filter, the featured item is already used */}
              <div className={`grid sm:grid-cols-2 lg:grid-cols-3 gap-5 ${(feature && !q && !activeTag) ? "mt-12" : ""}`} data-testid="insights-grid">
                {((feature && !q && !activeTag) ? rest : data.items).map((a) => (
                  <ArticleCard key={a.slug} article={a} />
                ))}
              </div>
            </>
          )}
        </section>
      </div>
    </MarketingLayout>
  );
}

function FeaturedCard({ article }) {
  return (
    <Link
      to={`/insights/${article.slug}`}
      className="group relative block rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 overflow-hidden hover:border-[#0055FF]/40 hover:shadow-[0_24px_60px_-24px_rgba(0,85,255,0.25)] transition-all"
      data-testid={`insights-featured-${article.slug}`}
    >
      <div className="grid lg:grid-cols-[1.15fr_1fr] gap-8 lg:gap-10 p-6 sm:p-8 lg:p-10">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0055FF] text-white px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em]">
              <Sparkles className="size-2.5" /> Featured
            </span>
            {article.sector_tags?.slice(0, 2).map((t) => (
              <span key={t} className="inline-flex items-center rounded-full bg-slate-100 border border-slate-200 px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.14em] text-slate-600">
                {t}
              </span>
            ))}
          </div>
          <h2 className="mt-4 text-3xl sm:text-4xl tracking-[-0.03em] font-semibold text-slate-900 leading-[1.05] group-hover:text-[#0044CC] transition-colors">
            {article.title}
          </h2>
          <p className="mt-4 text-[15px] text-slate-600 leading-relaxed max-w-xl">
            {article.excerpt}
          </p>
          <div className="mt-6 flex items-center gap-3 text-[12.5px] text-slate-500">
            <span className="font-medium text-slate-700">{article.author}</span>
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-1"><Clock className="size-3" /> {article.reading_time_mins} min read</span>
            <span className="text-slate-300">·</span>
            <span>{formatDate(article.published_at)}</span>
          </div>
          <div className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0055FF]">
            Read article <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
          </div>
        </div>
        <div className="hidden lg:block relative">
          <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#0055FF]/10 via-[#3d7bff]/5 to-emerald-500/10 border border-slate-200" />
          <div className="absolute inset-6 rounded-lg bg-white shadow-sm p-5 flex flex-col">
            <div className="text-[9.5px] font-mono uppercase tracking-[0.18em] text-slate-400">Reading now</div>
            <div className="mt-2 text-[14px] font-semibold text-slate-900 leading-snug">{article.title}</div>
            <div className="mt-3 space-y-1">
              {(article.excerpt || "").split(". ").slice(0, 2).map((line, i) => (
                <div key={i} className="h-1.5 rounded-full bg-slate-100 relative overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#0055FF] to-[#3d7bff]" style={{ width: `${75 - i * 15}%` }} />
                </div>
              ))}
            </div>
            <div className="mt-auto flex items-center justify-between text-[10.5px] font-mono text-slate-500 tabular-nums pt-4 border-t border-slate-100">
              <span>{article.reading_time_mins}m read</span>
              <span className="text-emerald-600">● live</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

function ArticleCard({ article }) {
  return (
    <Link
      to={`/insights/${article.slug}`}
      className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#0055FF]/40 hover:shadow-[0_16px_40px_-20px_rgba(0,85,255,0.2)] hover:-translate-y-0.5 transition-all"
      data-testid={`insights-card-${article.slug}`}
    >
      <div className="flex items-center gap-1.5 flex-wrap">
        {article.sector_tags?.slice(0, 2).map((t) => (
          <span key={t} className="inline-flex items-center rounded-full bg-slate-50 border border-slate-200 px-2 py-0.5 text-[9.5px] font-medium uppercase tracking-[0.14em] text-slate-500">
            {t}
          </span>
        ))}
      </div>
      <h3 className="mt-4 text-[19px] tracking-[-0.02em] font-semibold text-slate-900 leading-tight group-hover:text-[#0044CC] transition-colors">
        {article.title}
      </h3>
      <p className="mt-2.5 text-[13.5px] text-slate-600 leading-relaxed line-clamp-3">
        {article.excerpt}
      </p>
      <div className="mt-auto pt-5 flex items-center justify-between text-[11.5px] text-slate-500">
        <span className="font-medium text-slate-700">{article.author}</span>
        <span className="inline-flex items-center gap-3">
          <span className="inline-flex items-center gap-1"><Clock className="size-3" /> {article.reading_time_mins}m</span>
          <span className="text-slate-300">·</span>
          <span>{formatDate(article.published_at, true)}</span>
        </span>
      </div>
    </Link>
  );
}

function EmptyState({ q, tag, clear }) {
  return (
    <div className="text-center py-20" data-testid="insights-empty">
      <span className="mx-auto grid place-items-center size-12 rounded-full bg-slate-100 text-slate-500">
        <Search className="size-6" />
      </span>
      <h3 className="mt-5 text-xl font-semibold text-slate-900">Nothing matches.</h3>
      <p className="mt-2 text-[14px] text-slate-500 leading-relaxed max-w-sm mx-auto">
        {q && <>No article matches <span className="font-mono text-slate-700">&quot;{q}&quot;</span></>}
        {q && tag && " with the "}
        {tag && <>tag <span className="font-mono text-slate-700">{tag}</span></>}
        {(q || tag) && ". Try clearing filters."}
      </p>
      {(q || tag) && (
        <button
          onClick={clear}
          className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-slate-300 hover:border-slate-400 px-4 h-9 text-[12.5px] font-medium text-slate-700"
          data-testid="insights-clear"
        >
          <X className="size-3.5" /> Clear filters
        </button>
      )}
    </div>
  );
}

function formatDate(iso, short = false) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (short) {
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    }
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return "";
  }
}
